<template>
  <v-container>
    <v-row v-if="!selectedCouponId">
      <v-col>
        <div class="d-flex ga-2 justify-end mt-6 mt-md-0 w-100">
          <v-btn
            v-if="hasAdminAccess"
            @click="$emit('show-add-coupon')"
            class="text-sm-body-2 mb-2 mb-sm-0 ml-sm-2"
            color="primary"
            prepend-icon="mdi-plus"
            variant="flat"
            text="Add Coupon"
            data-testid="add-coupon-btn"
          />
        </div>
      </v-col>
      <v-col cols="12">
        <v-card class="data-list">
          <v-card-text>
            <v-data-table
              :items="displayedCoupons"
              item-key="id"
              class="elevation-1"
              :loading="loading"
              :headers="[
                { title: 'Title', key: 'title' },
                { title: 'Created', key: 'createdAt' },
                { title: 'Starts', key: 'startTime' },
                { title: 'Expires', key: 'endTime' },
                { title: 'Status', key: 'status' },
                { title: 'Recurring', key: 'recurring' },
              ]"
              v-model:search="couponSearch"
              data-testid="coupons-table"
            >
              <template v-slot:item="{ item }">
                <tr
                  style="cursor: pointer"
                  :class="item.status === 'Archived' ? 'archived-row' : ''"
                  @click="editCoupon(item.couponId)"
                >
                  <td>
                    <span>{{ item.title }}</span>
                  </td>
                  <td>{{ item?.createdAt }}</td>
                  <td>{{ item?.startTime }}</td>
                  <td>{{ item?.endTime }}</td>
                  <td>
                    <span
                      v-if="
                        ['Draft', 'Archived', 'Paused'].includes(item.status)
                      "
                      @click.stop="
                        () => {
                          pauseReason = item.reason || 'No reason provided'
                          showPausedReason = true
                        }
                      "
                      class="paused-text"
                    >
                      <span>{{ item.status }}</span>
                    </span>
                    <span v-else>
                      {{ item.status }}
                    </span>
                  </td>
                  <td>
                    <span @click="goToLogActivity(item.couponId)">
                      {{ item?.isRecurring ? 'Yes' : 'No' }}
                    </span>
                  </td>
                </tr>
              </template>

              <!-- Toolbar Slot -->
              <template v-slot:top>
                <div style="background-color: #eeeeee">
                  <v-row style="margin: 0; padding: 10px 13px">
                    <v-col cols="12" md="6" style="padding: 0">
                      <v-text-field
                        v-model="couponSearch"
                        label="Search Coupons"
                        prepend-inner-icon="mdi-magnify"
                        variant="solo-filled"
                        flat
                        hide-details
                        dense
                        density="compact"
                        data-testid="coupon-search-input"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      md="6"
                      class="align-stretch align-sm-center d-flex flex-column-reverse flex-sm-row justify-center justify-sm-end mt-2 mt-md-0 pa-0"
                    >
                      <v-btn
                        class="text-sm-body-2"
                        text="Archived Coupons"
                        variant="flat"
                        :color="showArchivedOnly ? 'primary' : ''"
                        @click="showArchivedOnly = true"
                        data-testid="archived-coupons-btn"
                      />
                      <v-btn
                        class="text-sm-body-2 mb-2 mb-sm-0 ml-sm-2"
                        text="All Coupons"
                        variant="flat"
                        :color="!showArchivedOnly ? 'primary' : ''"
                        @click="showArchivedOnly = false"
                        data-testid="all-coupons-btn"
                      />
                    </v-col>
                  </v-row>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <!-- Notification popup -->
  <v-snackbar v-model="snackbar.visible" :timeout="3000" color="success" top>
    {{ snackbar.message }}
  </v-snackbar>

  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="showPausedReason" max-width="500px">
    <v-card>
      <v-card-title>
        <span class="headline">Paused Reason</span>
      </v-card-title>
      <v-card-text>
        {{ pauseReason }}
      </v-card-text>
      <v-card-actions>
        <v-btn @click="showPausedReason = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useCounterStore } from '@/stores/counter'

const store = useCounterStore()
// const router = useRouter()
// const route = useRoute()

const emit = defineEmits<{
  'update:couponId': [couponId: string | null]
  'show-add-coupon': []
}>()

const showPausedReason = ref(false)
const snackbar = ref({
  visible: false,
  message: '',
})

const loading = ref(true)
const couponSearch = ref('')
const selectedCouponId = ref<string | null>(null)
const showArchivedOnly = ref(false)
defineProps({
  item: Object,
  hasAdminAccess: {
    type: Boolean,
    default: false,
  },
})

onMounted(async () => {
  await store.fetchAndSetCoupons()
  loading.value = false
  // const { update, add } = route.query
  //
  // if (update === 'success') {
  //   snackbar.value.visible = true
  //   snackbar.value.message = 'Coupon Deleted successfully!'
  //
  //   router.replace({ query: { ...route.query, update: undefined } })
  // }
  // if (add === 'success') {
  //   snackbar.value.visible = true
  //   snackbar.value.message = 'Coupon added successfully!'
  //   router.replace({ query: { ...route.query, add: undefined } })
  // }
})

const dialog = ref({
  visible: false,
  couponId: null,
})

const pauseDialog = ref({
  visible: false,
  reason: '',
  coupon: null,
})

const pauseReason = ref('')

function makeDate(str: string) {
  // ISO without seconds, assume UTC
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(str)) {
    return new Date(str + ':00Z')
  }

  // 2) "YYYY-MM-DD hh:mm AM|PM"
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(str)) {
    return new Date(str) // treat as local, NOT add ":00Z"
  }
  // if (m) {
  //   let [, year, mon, day, h, min, ampm] = m
  //   let hour = parseInt(h, 10)
  //   if (ampm.toUpperCase() === 'PM' && hour !== 12) hour += 12
  //   if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0
  //   return new Date(
  //     parseInt(year, 10),
  //     parseInt(mon, 10) - 1,
  //     parseInt(day, 10),
  //     hour,
  //     parseInt(min, 10),
  //   )
  // }

  // Any other (fully-qualified ISO, numeric timestamp, etc.)
  return new Date(str)
}
const displayedCoupons = computed(() => {
  // const start = parseIsoWithSeconds(c.startTime)
  // const end   = parseIsoWithSeconds(c.endTime)
  const coupons = store.coupons ?? []
  const rows = coupons.map(c => ({
    title: c.design?.title,
    createdAt: c.creationDate ? makeDate(c.creationDate).toLocaleString() : '',
    startTime: makeDate(c.startTime).toLocaleString(),
    endTime: makeDate(c.endTime).toLocaleString(),
    status: c.status, // 🠔 keep status
    isRecurring: c?.isRecurring,
    couponId: c.couponId,
    reason: c.reason,
  }))

  if (showArchivedOnly.value) {
    // 𝗔  archived view
    return rows.filter(r => r.status === 'Archived')
  }

  // 𝗕  all-coupons view  – sort so archived come last (optional)
  return rows.sort(
    (a, b) => Number(a.status === 'Archived') - Number(b.status === 'Archived'),
  )
})

const editCoupon = async (couponId: any) => {
  selectedCouponId.value = couponId
  emit('update:couponId', couponId)
}

const goToLogActivity = async (couponId: any) => {
  // router.push({ name: 'recurring', params: { couponId } })
}
</script>
